import React from 'react';
import * as Sentry from "@sentry/react";
import { useTranslation } from 'react-i18next';
import { shouldInitializeSentry } from '../instrument';

interface CrashFallbackProps {
  error: any;
  componentStack: string | null;
  eventId: string | null;
  resetError: () => void;
}

export const CrashFallback: React.FC<CrashFallbackProps> = ({ error, resetError, eventId }) => {
  const [feedback, setFeedback] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [showDevError, setShowDevError] = React.useState(false);
  const { t } = useTranslation();

  const handleReload = async () => {
    if (feedback.trim() && shouldInitializeSentry) {
      setIsSubmitting(true);
      try {
        await Sentry.captureFeedback({
          message: feedback,
          associatedEventId: eventId || Sentry.lastEventId(),
        });
        setIsSuccess(true);
      } catch (err) {
        console.error("Failed to send feedback", err);
      }
      setTimeout(() => {
        try {
          resetError();
        } catch (e) {
          // ignore
        }
        window.location.assign(window.location.pathname);
      }, 600);
    } else {
      window.location.assign(window.location.pathname);
    }
  };

  return (
    <div className="crash-screen">
      {/* Dev Error Toggle */}
      {import.meta.env.DEV && (
        <div className="crash-dev-toggle">
          <button
            className="crash-dev-btn"
            onClick={() => setShowDevError(!showDevError)}
          >
            {showDevError ? t('crashFallback.hide_error') : t('crashFallback.error_details')}
          </button>

          {showDevError && (
            <div className="crash-dev-panel">
              <strong>{t('crashFallback.dev_error')}</strong> {error.message}
            </div>
          )}
        </div>
      )}

      <div className="crash-content">
        <h1 className="crash-title">{t('crashFallback.oops')}</h1>

        <div className="crash-image-wrap">
          <img src="/crashed.png" alt="Crashed" />
        </div>

        <p className="crash-description">{t('crashFallback.description')}</p>

        <div className="crash-textarea-wrap">
          <textarea
            className="crash-textarea"
            placeholder={t('crashFallback.placeholder')}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={isSubmitting || isSuccess}
          />
        </div>

        <div className="crash-actions">
          <button
            className={`crash-reload-btn${isSuccess ? ' success' : ''}`}
            onClick={handleReload}
            disabled={isSubmitting || isSuccess}
          >
            {isSubmitting
              ? t('crashFallback.sending')
              : isSuccess
              ? t('crashFallback.received')
              : feedback.trim()
              ? t('crashFallback.send_and_reload')
              : t('crashFallback.reload_studio')}
          </button>
        </div>
      </div>
    </div>
  );
};
